// ============================================================
// usePresence.ts — React glue for the presence singleton.
//
// usePresenceConnection(): mount ONCE (in App). Drives the socket from
//   auth + the open case: connects on login, disconnects on logout, and
//   sends join/leave as currentCase changes.
//
// useCaseViewers(): subscribe to the viewer list for the open case.
//   Used by ViewingPaneHeader to show "also viewing".
//
// Presence is additive — these never throw; if the socket is down the
// viewer list is simply empty.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react'
import { receiveRemoteClaim } from './claimLive'
import { useCasevoStore } from '../store/useCasevoStore'
import { presence, type PresenceViewer, type PresenceFieldOwner, type PresenceEntityChanged, type PresenceFieldCaret } from '../services/presence'
import { applyRemoteCaseEdit, flushAutoSave } from './useAutoSave'
import { applyRemoteClientEdit, flushClientAutoSave, isClientSyncField } from './useClientAutoSave'
import { applyRemoteWitnessEdit, flushWitnessAutoSave } from './useWitnessAutoSave'
import { applyRemoteDeadlineEdit, flushDeadlineAutoSave } from './useCaseDeadlineAutoSave'
import { applyRemoteTaskEdit } from './useTaskAutoSave'
import { getCaseBaseUpdatedAt, getClientBaseUpdatedAt, getWitnessBaseUpdatedAt, getDeadlineBaseUpdatedAt, setTaskBaseUpdatedAt, setClientBaseUpdatedAt, setWitnessBaseUpdatedAt, setWitnessBaseValues, setDeadlineBaseUpdatedAt, setDeadlineBaseValues } from './saveBaseVersion'
import { listWitnessesApi, listCaseDeadlinesApi } from '../api/casevoApi'
import { useSectionCompletion } from '../store/useSectionCompletion'
import { getVisibleSequentialKeys, getNextSequentialKey } from '../utils/sectionFlow'
import { resolveCaseField } from './caseFieldRegistry'
import type { Client } from '../types/client.types'
import type { Witness } from '../types/case.types'
import type { CaseDeadline } from '../types/deadlines.types'
import type { Task } from '../types/tasks.types'

export const usePresenceConnection = (): void => {
  const isAuthenticated = useCasevoStore((s) => s.isAuthenticated)
  const authUser = useCasevoStore((s) => s.authUser)

  // Connect on login / disconnect on logout. The ROOM (which case/client/
  // task is open) is no longer driven here — each surface owns its room via
  // usePresenceRoom, so exactly one room-setter is mounted at a time.
  useEffect(() => {
    if (!isAuthenticated || !authUser) { presence.disconnect(); return }
    const token = localStorage.getItem('casevo_token')
    if (!token) return
    presence.connect(token, {
      userId: authUser.id,
      name: `${authUser.firstName} ${authUser.lastName}`.trim() || authUser.email,
      avatarColour: authUser.avatarColour,
      avatarStyle: authUser.avatarStyle,
      avatarPhotoUrl: authUser.avatarPhotoUrl,
    })
    // No cleanup here: the connection persists across navigation; the
    // logout branch above (isAuthenticated false) is what disconnects.
  }, [isAuthenticated, authUser])
}

// Declare the presence ROOM for the surface that mounts this hook:
// `case:<id>` / `client:<id>` / `task:<id>`, or null when nothing's open.
// Joins on mount/change and leaves on unmount. Because the app shows ONE
// entity surface at a time (takeovers replace each other), exactly one
// usePresenceRoom is active at any moment — no competing room-setters.
// setActiveRoom is idempotent + guarded, so this is safe when the socket
// is down (it just no-ops).
export const usePresenceRoom = (roomId: string | null): void => {
  useEffect(() => {
    presence.setActiveRoom(roomId)
    return () => { presence.setActiveRoom(null) }
  }, [roomId])
}

export const useCaseViewers = (): PresenceViewer[] => {
  const [viewers, setViewers] = useState<PresenceViewer[]>(presence.getViewers())
  useEffect(() => presence.subscribe(setViewers), [])
  return viewers
}

// Phase 2a: the field-owner list for the open case (who's editing which
// field). Subscribed by FormField's presence indicator. Additive — empty
// when the socket is down.
export const useFieldOwners = (): PresenceFieldOwner[] => {
  const [owners, setOwners] = useState<PresenceFieldOwner[]>(presence.getFieldOwners())
  useEffect(() => presence.subscribeFieldOwners(setOwners), [])
  return owners
}

// Phase 2a: a stable callback the delegated focus listener calls to
// report the local user's focused field (a fieldKey) or blur (null). Pure
// display-signalling — never touches the field's value / save path.
export const useFieldFocusReporter = (): ((fieldKey: string | null) => void) => {
  return useCallback((fieldKey: string | null) => {
    presence.setFocusedField(fieldKey)
  }, [])
}

// Phase 2b: ONE document-level focusin listener, mounted once for the whole
// case-detail view (by ViewingPane) so focus in ANY section reports — not
// just whichever section is currently rendered. focusin bubbles to the
// document, so a single listener catches every field regardless of which
// section component is mounted. It's scoped by data-field: focus landing
// inside a [data-field] wrapper (set by FormField when given a fieldKey)
// reports that field's key; focus anywhere else reports blur (the user
// left a tracked field). setFocusedField itself no-ops unless a case is
// open (guarded on activeCaseId), so this is harmless on the case list.
// Display-only — never touches any field's value or the save path.
export const useFieldFocusDelegation = (): void => {
  const reportFocus = useFieldFocusReporter()
  useEffect(() => {
    const onFocusIn = (e: FocusEvent): void => {
      const target = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
      reportFocus(target?.dataset.field ?? null)
    }
    // ★ focusin alone reports blur only when focus MOVES TO another (non-field)
    // focusable element. A click on the page BACKGROUND moves focus to nothing
    // focusable, so no focusin fires and the user's field-presence lingered
    // until a later focusin or a section change (which unmounts → cleanup
    // below). Most visible on the notes editor: clicking off the TipTap
    // contenteditable to the same page left the notes presence stuck for others.
    // focusout fires whenever a [data-field] loses focus; if focus is NOT going
    // to another [data-field] (relatedTarget is null / body / any non-field
    // element), report blur immediately. Field→field keeps relatedTarget inside
    // a [data-field], so we skip the clear there and let focusin report the new
    // field — normal field-to-field movement is unaffected (no flicker).
    const onFocusOut = (e: FocusEvent): void => {
      const from = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
      if (!from) return // focus left a non-tracked element — nothing to clear
      const to = (e.relatedTarget as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
      if (!to) reportFocus(null)
    }
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      reportFocus(null)
    }
  }, [reportFocus])
}

// ── Stage 1a: on-blur live edit-sync for CLIENT DETAILS (both surfaces) ──
// Proves the sync engine on the two surfaces whose fieldKey suffix IS the
// store field (no reverse-mapping registry needed):
//   • ClientSearchView live client   → fieldKey `client.<field>`       → selectedClient
//   • Case clientDetails snapshot     → fieldKey `clientDetails.<field>` → currentCase.clientDetails
//
// SEND (document focusout): when the user blurs a tracked client-details
// field, we (1) FLUSH the relevant autosave so the value is persisted and its
// optimistic-concurrency baseline advances to the new SERVER updatedAt, THEN
// (2) broadcast the field's value + that server updatedAt. Doing the broadcast
// AFTER the save resolves is what makes the concurrency reconciliation hold:
// co-editors advance their base to a value that already exists on the server.
//
// RECEIVE (subscribeFieldEdits): apply the remote value via the matching
// applyRemote*Edit (which advances baselines so NO save-loop fires and the
// base updatedAt moves forward), UNLESS the fieldKey is the one the local user
// currently has focused — applying mid-edit would clobber their caret; on the
// on-blur model it reconciles on their own next blur/reload.
//
// Everything here is additive and degrades gracefully: sendFieldEdit no-ops
// when the socket is down, the flushes are the normal save path, and a missing
// baseUpdatedAt simply skips the broadcast (editing is unaffected).

// Remember the last value we broadcast per fieldKey so a focus-through with no
// actual edit (tabbing past a field) doesn't emit a redundant edit message.
const lastSentByField = new Map<string, string>()

// ── Near-live "watch as you type" (generalised) ──────────────────────
// Only TEXT-TYPING controls broadcast on a typing debounce. Structured
// controls (select, date, checkbox, radio, colour, file, range) don't emit
// meaningful intermediate character states — so we exclude them from the typing
// path and let them broadcast on `change` (immediate) with focusout as a
// backstop (see the onChange + onFocusOut listeners below). NOTE: a bare blur
// backstop is NOT enough on its own for a reveal control — a checkbox/radio
// keeps focus after being toggled, so focusout may never fire until the user
// clicks elsewhere; the `change` listener is what makes such a control's value
// (e.g. the exceptionally-severe reveal boolean) reach other viewers live. The
// TipTap notes/rich-text editor is a contenteditable <div> (not input/textarea
// and it never fires `change`), so it is NOT caught by any of these paths — it
// keeps its own bespoke debounce, and flushReadBroadcast no-ops on `client.notes`
// anyway, so notes is never double-sent.
const TEXTUAL_INPUT_TYPES = new Set<string>([
  'text', 'search', 'email', 'tel', 'url', 'number', 'password',
])
const isTypingElement = (el: EventTarget | null): boolean => {
  const node = el as HTMLElement | null
  if (!node) return false
  if (node.tagName === 'TEXTAREA') return true
  if (node.tagName === 'INPUT') return TEXTUAL_INPUT_TYPES.has((node as HTMLInputElement).type)
  return false
}
const INPUT_BROADCAST_DEBOUNCE_MS = 400

// The live-CLIENT surface field for a `client.<field>` key, or null. Only
// fields the client save path persists (excludes client.notes, which has its
// own autosave). The CASE room (clientDetails.* + the 9 case sections) is
// resolved separately via resolveCaseField (caseFieldRegistry).
const clientSurfaceField = (key: string): string | null => {
  if (!key.startsWith('client.')) return null
  const field = key.slice('client.'.length)
  return isClientSyncField(field) ? field : null
}

// ── Stage 1c: WITNESS / DEADLINE row-scoped keys ─────────────────────
// The list sections key each row's fields `witness.<id>.<field>` /
// `deadline.<id>.<field>`. Unlike the case sections, these live in their OWN
// per-row autosave singletons (useWitnessAutoSave / useCaseDeadlineAutoSave)
// with per-row concurrency bases, so they route to their own remote-appliers.
//
// WITNESS field suffix IS the store column (name/addressLine1…/phone/email);
// `address-search` is the lookup box and is excluded. `statement` is now a Yjs
// collab rich-text field (Stage 3) — it syncs via Hocuspocus (doc
// `witness:<id>:statement`), NOT the near-live field-edit engine, so it is
// intentionally omitted here (parseRowKey returns null for it → no double-sync).
// The FormField focus/presence indicator still works (separate focus channel).
// DEADLINE: `due-date` syncs near-live (slug→dueDate). `notes` is now a Yjs
// collab rich-text field (Stage 4, doc `deadline:<id>:notes`) — omitted here so
// parseRowKey returns null for it (no double-sync); its FormField focus/presence
// indicator still works. `completed` is toggled by a separate immediate-PATCH
// button with no data-field, so it is not live-synced here.
const WITNESS_FIELDS = new Set<string>([
  'name', 'addressLine1', 'addressLine2', 'addressTown', 'addressCounty',
  'addressPostcode', 'phone', 'email',
])
const DEADLINE_FIELD_MAP: Record<string, keyof CaseDeadline> = {
  'due-date': 'dueDate',
}
type RowKey =
  | { kind: 'witness'; id: string; field: keyof Witness }
  | { kind: 'deadline'; id: string; field: keyof CaseDeadline }
const parseRowKey = (key: string): RowKey | null => {
  if (key.startsWith('witness.')) {
    const rest = key.slice('witness.'.length)
    const dot = rest.indexOf('.')
    if (dot < 0) return null
    const id = rest.slice(0, dot)
    const field = rest.slice(dot + 1)
    return WITNESS_FIELDS.has(field) ? { kind: 'witness', id, field: field as keyof Witness } : null
  }
  if (key.startsWith('deadline.')) {
    const rest = key.slice('deadline.'.length)
    const dot = rest.indexOf('.')
    if (dot < 0) return null
    const id = rest.slice(0, dot)
    const col = DEADLINE_FIELD_MAP[rest.slice(dot + 1)]
    return col ? { kind: 'deadline', id, field: col } : null
  }
  return null
}

// Locate a loaded row by id across the per-case maps (the edited row belongs to
// the open case, but searching all lists keeps this independent of which case
// is "current" and tolerates a late message).
const findWitnessRow = (id: string): Witness | undefined => {
  for (const list of Object.values(useCasevoStore.getState().witnesses)) {
    const found = list.find((w) => w.id === id)
    if (found) return found
  }
  return undefined
}
const findDeadlineRow = (id: string): CaseDeadline | undefined => {
  for (const list of Object.values(useCasevoStore.getState().caseDeadlines)) {
    const found = list.find((d) => d.id === id)
    if (found) return found
  }
  return undefined
}

// Flush the relevant autosave, read the persisted value + the advanced server
// updatedAt, then broadcast — the shared "send" for ONE tracked field key.
// Used both by the document focusout listener (manual edit + blur) and by
// broadcastFieldEdits (programmatic multi-field updates, e.g. address lookup).
// Two routes:
//   • client.<field>  → the live-client surface (selectedClient / client save).
//   • everything else → the CASE room via resolveCaseField, which covers the
//     clientDetails snapshot AND all 9 case sections (label-slug fieldKeys
//     mapped to their store fields by the registry). One fieldKey may own
//     several columns (injury grid, accident-location) — entry.read returns the
//     whole group, entry.apply writes it back.
// Safe + best-effort: no-ops for non-syncable keys, when the surface isn't
// loaded, when no base updatedAt is captured yet, or when the socket is down
// (sendFieldEdit guards that). The per-key dedupe (serial = updatedAt + value)
// avoids emitting a redundant message when nothing actually changed.
const flushReadBroadcast = async (key: string): Promise<void> => {
  const clientField = clientSurfaceField(key)
  if (clientField) {
    await flushClientAutoSave()
    const c = useCasevoStore.getState().selectedClient
    if (!c) return
    const updatedAt = getClientBaseUpdatedAt(c.id)
    if (!updatedAt) return
    const value = (c as unknown as Record<string, unknown>)[clientField]
    const serial = `${updatedAt}::${JSON.stringify(value ?? null)}`
    if (lastSentByField.get(key) === serial) return
    lastSentByField.set(key, serial)
    presence.sendFieldEdit(key, value, updatedAt)
    return
  }
  // Witness / deadline list rows — own per-row autosave + base.
  const row = parseRowKey(key)
  if (row) {
    if (row.kind === 'witness') {
      await flushWitnessAutoSave()
      const w = findWitnessRow(row.id)
      if (!w) return
      const updatedAt = getWitnessBaseUpdatedAt(row.id)
      if (!updatedAt) return
      const value = (w as unknown as Record<string, unknown>)[row.field]
      const serial = `${updatedAt}::${JSON.stringify(value ?? null)}`
      if (lastSentByField.get(key) === serial) return
      lastSentByField.set(key, serial)
      presence.sendFieldEdit(key, value, updatedAt)
    } else {
      await flushDeadlineAutoSave()
      const d = findDeadlineRow(row.id)
      if (!d) return
      const updatedAt = getDeadlineBaseUpdatedAt(row.id)
      if (!updatedAt) return
      const value = (d as unknown as Record<string, unknown>)[row.field]
      const serial = `${updatedAt}::${JSON.stringify(value ?? null)}`
      if (lastSentByField.get(key) === serial) return
      lastSentByField.set(key, serial)
      presence.sendFieldEdit(key, value, updatedAt)
    }
    return
  }
  const entry = resolveCaseField(key)
  if (!entry) return
  await flushAutoSave()
  const cc = useCasevoStore.getState().currentCase
  if (!cc) return
  const updatedAt = getCaseBaseUpdatedAt(cc.id)
  if (!updatedAt) return
  const value = entry.read(cc)
  const serial = `${updatedAt}::${JSON.stringify(value ?? null)}`
  if (lastSentByField.get(key) === serial) return
  lastSentByField.set(key, serial)
  presence.sendFieldEdit(key, value, updatedAt)
}

// Imperative broadcast for fields changed PROGRAMMATICALLY — i.e. not via a
// typed edit that fires focusout. The prime case is the address lookup:
// picking a suggestion (or Clear) writes all 5 address fields in one store
// mutation with NO focus/blur on the individual inputs, so the focusout sender
// below never sees them and they don't propagate. Call this right after such a
// multi-field update with the affected fieldKeys.
//
// Deferred one macrotask so the programmatic store update is first picked up by
// the autosave effect (which stages the debounced save); flushReadBroadcast
// then FORCES that save via flush, so the broadcast carries the fresh post-save
// server updatedAt (mechanic 2 holds — peers advance to a value that exists on
// the server). Additive + best-effort throughout.
export const broadcastFieldEdits = (fieldKeys: string[]): void => {
  setTimeout(() => {
    void (async () => {
      for (const key of fieldKeys) await flushReadBroadcast(key)
    })()
  }, 0)
}

// Back-compat alias — the client-details sections import this name. Same
// implementation; it was never client-specific (flushReadBroadcast routes by
// key), so case sections use broadcastFieldEdits directly.
export const broadcastClientFieldEdits = broadcastFieldEdits

// ── Immediate (non-awaiting) broadcast for reveal-type case fields ───
// flushReadBroadcast (used by the change/blur listeners) does `await
// flushAutoSave()` first, which BLOCKS on any in-flight case save before it
// sends. That's fine for most fields, but a REVEAL control (the exceptionally-
// severe checkbox) shows/hides a whole sub-editor: while the box is open there's
// often a case save in flight (the collab editor's HTML-mirror snapshot, or the
// tick's own debounced save), so UNTICK would wait for it and the box would
// disappear on other screens seconds after it appeared. This variant sends
// RIGHT NOW — it reads the current store value (already correct; the checkbox's
// onChange updated it synchronously) and the current server base, and emits
// without awaiting the flush, so the reveal propagates with equal promptness in
// BOTH directions. Correctness is preserved: the general `change`-listener path
// still runs flushReadBroadcast and re-broadcasts with the post-save base
// (deduped if unchanged), and any transient base lag on the receiver self-heals
// via the column-aware 409 recovery (a boolean flip touches only its own
// column). Case-room keys only (resolveCaseField); a no-op otherwise.
export const broadcastFieldEditNow = (key: string): void => {
  const entry = resolveCaseField(key)
  if (!entry) return
  const cc = useCasevoStore.getState().currentCase
  if (!cc) return
  const updatedAt = getCaseBaseUpdatedAt(cc.id)
  if (!updatedAt) return
  const value = entry.read(cc)
  const serial = `${updatedAt}::${JSON.stringify(value ?? null)}`
  if (lastSentByField.get(key) === serial) return
  lastSentByField.set(key, serial)
  presence.sendFieldEdit(key, value, updatedAt)
}

// ── Stage B: live form-field cursors — ALL eligible plain text inputs ──
// The mirror-measure cursor (proven on the Forename pilot) now applies to EVERY
// eligible single-line text <input>. Eligibility = an <input> whose type is one
// of these (its selection offset is a usable caret index):
export const CARET_ELIGIBLE_INPUT_TYPES = new Set<string>(['text', 'search', 'email', 'tel', 'url', 'number'])
// EXCLUDED (and why): password (sensitive); checkbox/radio/date/time/file/range/
// color and <select> (no meaningful text caret); <textarea> (multi-line mirror is
// a later stage); the Yjs RichTextEditor fields (contenteditable — no <input>, so
// naturally skipped, and they already have a CollaborationCaret); and the
// address-SEARCH boxes (transient lookups — excluded by fieldKey below).
const isEligibleCaretInput = (el: EventTarget | null): el is HTMLInputElement => {
  const n = el as HTMLElement | null
  return !!n && n.tagName === 'INPUT' && CARET_ELIGIBLE_INPUT_TYPES.has((n as HTMLInputElement).type)
}
// A field participates in live cursors when it has a fieldKey that isn't an
// address-search box. Shared by the sender + FormField's overlay mount so both
// gate identically.
export const isCaretCursorEligibleFieldKey = (key: string | undefined | null): boolean =>
  !!key && !key.includes('address-search')

// SEND: broadcast the local user's caret for WHICHEVER eligible text field they
// have focused (one at a time — only document.activeElement), THROTTLED (~60ms).
// Fires on selectionchange (caret move via arrows/click/type), input, keyup, and
// focusin (first caret on entry). Best-effort — no-ops when the socket's down;
// never touches the field's value/save path. Mount ONCE (App.tsx).
export const useFieldCaretSync = (): void => {
  useEffect(() => {
    const THROTTLE_MS = 60
    let pending: number | null = null
    const flush = (): void => {
      pending = null
      const el = document.activeElement
      if (!isEligibleCaretInput(el)) return
      const wrap = (el as HTMLElement).closest('[data-field]') as HTMLElement | null
      const key = wrap?.dataset.field
      if (!isCaretCursorEligibleFieldKey(key)) return
      const input = el as HTMLInputElement
      // selectionStart can throw / be null on some input types (e.g. number in
      // Chrome) — guard and fall back to end-of-text so it degrades, never crashes.
      let selStart: number
      let selEnd: number
      try {
        selStart = input.selectionStart ?? input.value.length
        selEnd = input.selectionEnd ?? selStart
      } catch {
        selStart = input.value.length
        selEnd = selStart
      }
      // Carry the sender's FULL field value every keystroke. It serves TWO
      // purposes on the receiver: (a) the overlay measures value[0..selStart] to
      // position the cursor, and (b) an app-level receiver applies the whole value
      // immediately so the TEXT updates as fast as the cursor (the ~400ms near-
      // live field-edit is only the persistence backstop). Full value (not just
      // the prefix) so the receiver has the complete text even mid-string.
      presence.sendFieldCaret(key as string, selStart, selEnd, input.value)
    }
    const schedule = (): void => { if (pending === null) pending = window.setTimeout(flush, THROTTLE_MS) }
    const onFocusIn = (e: FocusEvent): void => { if (isEligibleCaretInput(e.target)) schedule() }
    // selectionchange is document-level (no target of interest) → schedule + let
    // flush read document.activeElement.
    document.addEventListener('selectionchange', schedule)
    document.addEventListener('input', schedule, true)
    document.addEventListener('keyup', schedule, true)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      if (pending !== null) window.clearTimeout(pending)
      document.removeEventListener('selectionchange', schedule)
      document.removeEventListener('input', schedule, true)
      document.removeEventListener('keyup', schedule, true)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [])
}

// RECEIVE: the latest remote caret for `fieldKey`, or null. Transient — cleared
// on room/field change; the overlay additionally gates on the field-owner
// presence so a blur (owner removed) hides the cursor even if a stale caret
// lingers.
export const useRemoteFieldCaret = (fieldKey: string): PresenceFieldCaret | null => {
  const [caret, setCaret] = useState<PresenceFieldCaret | null>(null)
  useEffect(() => {
    setCaret(null)
    const unsub = presence.subscribeFieldCaret((c) => {
      if (c.fieldKey === fieldKey) setCaret(c)
    })
    return () => { unsub(); setCaret(null) }
  }, [fieldKey])
  return caret
}

// FAST TEXT: apply the sender's full field value from the caret message to the
// store IMMEDIATELY, so the remote text updates in lock-step with the remote
// cursor (instead of lagging behind on the ~400ms near-live path). Routes by
// fieldKey to the SAME per-surface store the near-live receiver uses, so EVERY
// store-backed live-cursor surface keeps pace: `client.<field>` → selectedClient,
// witness/deadline rows → their per-row store, and the CASE room (clientDetails.*
// + the 9 case sections) → currentCase. (Task form values live in LOCAL form
// state, not a store, so tasks apply their fast text inside TasksView's own
// caret receiver.) Mount ONCE (App.tsx).
//
// The near-live field-edit stays the persistence backstop (it re-applies the same
// value later WITH the real server updatedAt, advancing the concurrency base
// correctly — this fast apply keeps the CURRENT base so it neither loops a save
// nor regresses the base). Guards on every surface: skip the field the LOCAL user
// is editing (never clobber their typing), require a captured base, and skip when
// the value hasn't actually changed (a caret-move-only message → no store churn).
export const useRemoteCaretTextApply = (): void => {
  useEffect(() => {
    const unsub = presence.subscribeFieldCaret((caret) => {
      const key = caret.fieldKey
      const value = caret.caretText
      // Never overwrite the field THIS user is actively editing.
      if (key === presence.getLocalFocusedField()) return
      const store = useCasevoStore.getState()

      // CLIENT surface (client.<field>) → the live-client store (selectedClient).
      const clientField = clientSurfaceField(key)
      if (clientField) {
        const c = store.selectedClient
        if (!c) return
        if ((c as unknown as Record<string, unknown>)[clientField] === value) return
        const base = getClientBaseUpdatedAt(c.id)
        if (!base) return
        applyRemoteClientEdit(c.id, { [clientField]: value } as unknown as Partial<Client>, base)
        return
      }

      // WITNESS / DEADLINE list rows → their own per-row appliers + base.
      const row = parseRowKey(key)
      if (row) {
        if (row.kind === 'witness') {
          const w = findWitnessRow(row.id)
          if (!w) return
          if ((w as unknown as Record<string, unknown>)[row.field] === value) return
          const base = getWitnessBaseUpdatedAt(row.id)
          if (!base) return
          applyRemoteWitnessEdit(row.id, { [row.field]: value } as Partial<Witness>, base)
        } else {
          const d = findDeadlineRow(row.id)
          if (!d) return
          if ((d as unknown as Record<string, unknown>)[row.field] === value) return
          const base = getDeadlineBaseUpdatedAt(row.id)
          if (!base) return
          applyRemoteDeadlineEdit(row.id, { [row.field]: value } as Partial<CaseDeadline>, base)
        }
        return
      }

      // CASE room — clientDetails snapshot + the 9 case sections.
      const entry = resolveCaseField(key)
      if (!entry) return
      const cc = store.currentCase
      if (!cc) return
      if (entry.read(cc) === value) return
      const base = getCaseBaseUpdatedAt(cc.id)
      if (!base) return
      applyRemoteCaseEdit(entry.apply(cc, value), base)
    })
    return unsub
  }, [])
}

export const useClientFieldEditSync = (): void => {
  useEffect(() => {
    // ── RECEIVE ──────────────────────────────────────────────
    const unsubscribe = presence.subscribeFieldEdits((edit) => {
      // Exclude the field the local user is editing (no caret clobber).
      if (edit.fieldKey === presence.getLocalFocusedField()) return
      const store = useCasevoStore.getState()
      const clientField = clientSurfaceField(edit.fieldKey)
      if (clientField) {
        const c = store.selectedClient
        if (!c) return
        applyRemoteClientEdit(
          c.id,
          { [clientField]: edit.value } as unknown as Partial<Client>,
          edit.updatedAt,
        )
        return
      }
      // Witness / deadline list rows — route to their own per-row appliers
      // (own autosave + per-row base; no save-loop since those hooks don't
      // subscribe to the store).
      const row = parseRowKey(edit.fieldKey)
      if (row) {
        if (row.kind === 'witness') {
          applyRemoteWitnessEdit(row.id, { [row.field]: edit.value } as Partial<Witness>, edit.updatedAt)
        } else {
          applyRemoteDeadlineEdit(row.id, { [row.field]: edit.value } as Partial<CaseDeadline>, edit.updatedAt)
        }
        return
      }
      // CASE room — clientDetails snapshot OR any of the 9 case sections,
      // resolved + applied via the registry (entry.apply builds the nested
      // Partial<Case>). applyRemoteCaseEdit advances the autosave baselines
      // so the apply stages ZERO saves (no loop).
      const entry = resolveCaseField(edit.fieldKey)
      if (!entry) return
      const cc = store.currentCase
      if (!cc) return
      applyRemoteCaseEdit(entry.apply(cc, edit.value), edit.updatedAt)
    })

    // ── SEND (blur → save → broadcast) ───────────────────────
    // Manual edits fire focusout on the field's own input; the shared
    // flushReadBroadcast does the flush→read→send. Programmatic multi-field
    // updates (address lookup) don't fire focusout — they call
    // broadcastClientFieldEdits directly (see ClientDetailsSection /
    // ClientSearchView address onSelect/onClear).
    // Per-fieldKey typing-debounce timers for the near-live input broadcast.
    // Keyed by fieldKey so ONLY the field currently being typed in is ever
    // scheduled — never a sweep over every field.
    const inputTimers = new Map<string, ReturnType<typeof setTimeout>>()
    const clearInputTimer = (key: string): void => {
      const t = inputTimers.get(key)
      if (t) { clearTimeout(t); inputTimers.delete(key) }
    }

    const onFocusOut = (e: FocusEvent): void => {
      const el = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
      const key = el?.dataset.field
      if (!key) return
      // Blur carries the final value now — drop any pending typing broadcast so
      // it can't fire a second (redundant) flush+send right after. The
      // lastSentByField dedupe already suppresses a duplicate SEND, but
      // cancelling also avoids a duplicate flush (save).
      clearInputTimer(key)
      // Fire-and-forget — any flush rejection is handled inside the autosave hook.
      void flushReadBroadcast(key)
    }
    document.addEventListener('focusout', onFocusOut)

    // ── SEND (debounced typing → save → broadcast) ───────────────
    // A single document-level 'input' listener generalises notes' "watch as you
    // type" to EVERY text field across all engine surfaces (client details,
    // case sections, witnesses, deadlines) with zero per-field wiring: it reads
    // the focused [data-field] and, after ~400ms of quiet, calls the SAME
    // flushReadBroadcast the blur path uses — which is already surface-aware
    // (flushes the right autosave, reads value + base, dedupes, sends). Text
    // inputs/textarea only (isTypingElement); structured controls fall through
    // to their instant change/blur sync. Only the actively-typed field is ever
    // scheduled. NOTE: flushReadBroadcast PERSISTS (flush) before sending, so a
    // debounced typing broadcast is a save + a send — the ~400ms debounce keeps
    // this to roughly one save per typing pause on the one field in focus.
    const onInput = (e: Event): void => {
      if (!isTypingElement(e.target)) return
      const el = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
      const key = el?.dataset.field
      if (!key) return
      clearInputTimer(key)
      inputTimers.set(key, setTimeout(() => {
        inputTimers.delete(key)
        void flushReadBroadcast(key)
      }, INPUT_BROADCAST_DEBOUNCE_MS))
    }
    document.addEventListener('input', onInput)

    // ── SEND (structured controls → immediate broadcast on change) ───
    // Checkboxes, radios, selects, date/time and other NON-typing controls
    // don't emit useful intermediate states (the input-debounce path skips them)
    // and can't be relied on to fire focusout promptly — a REVEAL checkbox keeps
    // focus after being ticked, so its value would never reach other viewers
    // until the user happened to click away. `change` fires the instant such a
    // control's value settles, so we broadcast immediately here via the SAME
    // flushReadBroadcast the blur path uses (flush → read persisted value + base
    // → dedupe → send). This is what makes the exceptionally-severe reveal
    // boolean (and other selects/radios) propagate LIVE. Typing elements are
    // excluded — they're carried by input+focusout, and their change-on-blur
    // would only duplicate focusout. If a later focusout fires for the same
    // field, lastSentByField dedupes it (serial = updatedAt + value) → no
    // double-send. The TipTap editor is a contenteditable <div> and never fires
    // `change`, so collab fields are untouched (and flushReadBroadcast no-ops on
    // their keys anyway).
    const onChange = (e: Event): void => {
      if (isTypingElement(e.target)) return
      const el = (e.target as HTMLElement | null)?.closest('[data-field]') as HTMLElement | null
      const key = el?.dataset.field
      if (!key) return
      clearInputTimer(key)
      void flushReadBroadcast(key)
    }
    document.addEventListener('change', onChange)

    return () => {
      unsubscribe()
      document.removeEventListener('focusout', onFocusOut)
      document.removeEventListener('input', onInput)
      document.removeEventListener('change', onChange)
      inputTimers.forEach((t) => clearTimeout(t))
      inputTimers.clear()
    }
  }, [])
}

// (Stage 3) The old client-driven useListUpdateSync has been removed — task/
// case list live-patching is now handled server-driven by useEntityChangeSync
// (op:'update') above, which also advances the concurrency base from `at`.

// ── Live-freshness: firm-wide entity CHANGE propagation (Stages 2+3) ──
// Mounted once (in App). On a remote entity-changed (echo-suppressed upstream —
// the actor already applied it locally), keep every STORE-BACKED list fresh
// without a refresh:
//   • op:'update' → PATCH the row by id from the event summary AND advance the
//     optimistic-concurrency base from `at` (so this account's next save of the
//     entity doesn't spuriously 409 — the same reconciliation the retired
//     client-driven list-update relay carried). Server-driven, so it catches
//     EVERY update path (incl. list-menu / context-menu edits the old relay
//     missed). Witness/deadline route through their applyRemote* appliers
//     (merge + base + base-values), scope-gated by whether that case's list is
//     loaded.
//   • op:'delete' → remove the row, and bounce the user off a record deleted
//     out from under them (open case/client → notice + back to the list).
// The two LOCAL-state surfaces are handled in their own components: the case
// list + the case op:'update'/'delete' patch in CaseListScreen, and the
// per-client activity timeline in ClientSearchView (a scoped refetch, as that
// feed is filtered + totalled). Best-effort + additive: absent lists / unknown
// ids are no-ops; socket down → the list is simply correct on the next load.
// Debounce for the favourites re-fetch triggered by remote case renames.
let favRefreshTimer: number | undefined
export const useEntityChangeSync = (): void => {
  useEffect(() => {
    return presence.subscribeEntityChanged((e) => {
      const store = useCasevoStore.getState()
      const summary = (e.summary ?? {}) as Record<string, unknown>

      // ★ (Batch C) CREATE of a case's witness / deadline by someone else:
      // previously nothing added it to an open case (only the calendar
      // refetched). If that case's list is loaded, fetch it and APPEND only the
      // rows we don't have — never overwrite rows (they may hold unsaved
      // local typing). Seeds the concurrency base for the new rows.
      if (e.op === 'create' && (e.entity === 'witness' || e.entity === 'deadline')) {
        const caseId = e.scope?.caseId
        if (!caseId) return
        if (e.entity === 'witness') {
          if (!store.witnesses[caseId]) return
          void listWitnessesApi(caseId).then((fresh) => {
            const cur = useCasevoStore.getState().witnesses[caseId]
            if (!cur) return
            const have = new Set(cur.map((w) => w.id))
            const add = fresh.filter((w) => !have.has(w.id))
            if (add.length === 0) return
            add.forEach((w) => { setWitnessBaseUpdatedAt(w.id, w.updatedAt); setWitnessBaseValues(w.id, w as unknown as Record<string, unknown>) })
            useCasevoStore.setState({ witnesses: { ...useCasevoStore.getState().witnesses, [caseId]: [...cur, ...add] } })
          }).catch(() => { /* best-effort — correct on next load */ })
        } else {
          if (!store.caseDeadlines[caseId]) return
          void listCaseDeadlinesApi(caseId).then((fresh) => {
            const cur = useCasevoStore.getState().caseDeadlines[caseId]
            if (!cur) return
            const have = new Set(cur.map((d) => d.id))
            const add = fresh.filter((d) => !have.has(d.id))
            if (add.length === 0) return
            add.forEach((d) => { setDeadlineBaseUpdatedAt(d.id, d.updatedAt); setDeadlineBaseValues(d.id, d as unknown as Record<string, unknown>) })
            useCasevoStore.setState({ caseDeadlines: { ...useCasevoStore.getState().caseDeadlines, [caseId]: [...cur, ...add] } })
          }).catch(() => { /* best-effort — correct on next load */ })
        }
        return
      }

      if (e.op === 'update') {
        switch (e.entity) {
          case 'task': {
            // ★ Access lost (stripped server event — id only): drop the row
            // live, never patch it. Mirrors the case-access redaction.
            if (e.revoked) {
              const list = store.tasks
              if (list && list.some((t) => t.id === e.id)) {
                useCasevoStore.setState({ tasks: list.filter((t) => t.id !== e.id) })
              }
              break
            }
            // (A task newly relevant to me that isn't listed yet is inserted
            // live by TasksView's own subscriber — this hook only patches rows.)
            // Keep the base current even if the row isn't in the cached list
            // (so opening it later doesn't 409), then patch the row if present.
            if (e.at) {
              setTaskBaseUpdatedAt(e.id, e.at)
              applyRemoteTaskEdit(e.id, summary as Partial<Task>, e.at)
            }
            break
          }
          case 'client': {
            if (e.at) setClientBaseUpdatedAt(e.id, e.at)
            const list = store.clients
            // (Audit batch 3, H4) A walled client's row is a bare stub: never
            // patch a name into it (the server sends walled viewers no summary).
            if (list && list.some((c) => c.id === e.id && !c.restricted)) {
              useCasevoStore.setState({
                clients: list.map((c) => (c.id === e.id ? { ...c, ...(summary as Partial<Client>) } : c)),
              })
            }
            break
          }
          case 'case': {
            // ★ Name source changed (rename / defendant / client snapshot) →
            // keep the sidebar live: patch the Recent Cases row from the event,
            // and re-fetch favourites (the server re-derives case labels live)
            // if this case is favourited. Debounced per burst.
            {
              const sn = summary as { caseName?: string | null; defendantName?: string | null; clientForename?: string | null; clientSurname?: string | null }
              if ('caseName' in sn || 'defendantName' in sn || 'clientSurname' in sn) {
                const rc = store.recentCases
                if (rc && rc.some((c) => c.id === e.id)) {
                  useCasevoStore.setState({
                    recentCases: rc.map((c) => (c.id === e.id ? {
                      ...c,
                      ...('caseName' in sn ? { caseName: sn.caseName ?? null } : {}),
                      ...('defendantName' in sn ? { defendantName: sn.defendantName ?? null } : {}),
                      ...('clientForename' in sn ? { clientForename: sn.clientForename ?? '' } : {}),
                      ...('clientSurname' in sn ? { clientSurname: sn.clientSurname ?? '' } : {}),
                    } as typeof c : c)),
                  })
                }
                if (store.favourites?.some((f) => f.itemType === 'case' && f.itemId === e.id)) {
                  window.clearTimeout(favRefreshTimer)
                  favRefreshTimer = window.setTimeout(() => { void useCasevoStore.getState().loadFavourites() }, 800)
                }
              }
            }
            // ★ (Batch C) Access LOST while the case is open (e.g. I was taken
            // off it): the list dropped the row but the open case stayed on
            // screen. Same audience rule as the list / server — bounce with a
            // notice. (Admins / seeAllCases always keep access.)
            if (store.currentCase?.id !== e.id) break
            // ★ Section-flow completion changed by someone else → apply live
            // (✓ marks, unlock of the next section, orange "current" marker),
            // using THIS viewer's visible section chain to find each "next".
            if (Array.isArray((summary as { completedSections?: unknown }).completedSections)) {
              const shared = (summary as { completedSections: string[] }).completedSections
              const visible = getVisibleSequentialKeys(store.viewingPaneToggles, store.userRole)
              useSectionCompletion.getState().applyRemoteCompletion(e.id, shared, (s) => getNextSequentialKey(s, visible))
              // Keep currentCase's copy in step (progress bars / re-open seed).
              useCasevoStore.setState({ currentCase: { ...store.currentCase, completedSections: shared } })
            }
            // (B2 polish) The Claim group changed by someone else → live
            // (never over an open Claim draft — hooks/claimLive).
            const claimSummary = (summary as { claim?: unknown }).claim
            if (claimSummary && typeof claimSummary === 'object' && e.at) {
              receiveRemoteClaim(e.id, claimSummary as Record<string, unknown>, e.actorUserId, e.at)
            }
            const me = store.authUser?.id
            const perms = store.authUser?.effectivePermissions
            if (!me || store.authUser?.role === 'admin' || perms?.seeAllCases) break
            const sc = summary as { assignedToUserId?: string | null; firmWide?: boolean; assignees?: { userId: string }[] }
            if (!('assignedToUserId' in sc) || !Array.isArray(sc.assignees)) break // no audience in this event
            const stillSees = sc.firmWide === true || sc.assignedToUserId === me || sc.assignees.some((a) => a.userId === me)
            if (!stillSees) {
              store.clearCurrentCase()
              store.setSaveConflict("You no longer have access to this case — returning you to the case list.")
            }
            break
          }
          case 'witness': {
            // Scope-gated by row presence — applyRemoteWitnessEdit no-ops when
            // this case's witnesses aren't loaded. Merges + advances base +
            // base-values. (Redundant with the room field-edit sync for a plain
            // field edit, but idempotent — and it catches non-blur paths.)
            if (e.at) applyRemoteWitnessEdit(e.id, summary as Partial<Witness>, e.at)
            break
          }
          case 'deadline': {
            // Same as witness — and this is what makes the "completed" toggle
            // (an immediate PATCH with no field-blur broadcast) live-sync.
            if (e.at) applyRemoteDeadlineEdit(e.id, summary as Partial<CaseDeadline>, e.at)
            break
          }
          default:
            break
        }
        return
      }

      if (e.op === 'delete') {
        switch (e.entity) {
          case 'task': {
            const list = store.tasks
            if (list && list.some((t) => t.id === e.id)) {
              useCasevoStore.setState({ tasks: list.filter((t) => t.id !== e.id) })
            }
            break
          }
          case 'witness': {
            const caseId = e.scope?.caseId
            if (!caseId) break
            const list = store.witnesses[caseId]
            if (list && list.some((w) => w.id === e.id)) {
              useCasevoStore.setState({
                witnesses: { ...store.witnesses, [caseId]: list.filter((w) => w.id !== e.id) },
              })
            }
            break
          }
          case 'deadline': {
            const caseId = e.scope?.caseId
            if (!caseId) break
            const list = store.caseDeadlines[caseId]
            if (list && list.some((d) => d.id === e.id)) {
              useCasevoStore.setState({
                caseDeadlines: { ...store.caseDeadlines, [caseId]: list.filter((d) => d.id !== e.id) },
              })
            }
            break
          }
          case 'client': {
            // Remove from the firm-wide clients[] cache (drives the client
            // search list in ClientSearchView — store-backed, so it updates).
            const list = store.clients
            if (list && list.some((c) => c.id === e.id)) {
              useCasevoStore.setState({ clients: list.filter((c) => c.id !== e.id) })
            }
            // Open-record edge: the user is viewing this client → bounce out
            // with a non-destructive notice so they're not left on a dead record.
            if (store.selectedClient?.id === e.id) {
              store.setSelectedClient(null)
              // (audit) actually return to the list — the detail view with no
              // client rendered a blank page despite the notice.
              store.setClientSearchMode('search')
              store.setSaveConflict('This client was deleted by someone else — returning you to the client list.')
            }
            break
          }
          case 'case': {
            // Open-record edge: the user has this case open in the detail view →
            // notice + clear it so App falls through to the case list (never a
            // dead record / crash). The case LIST row is removed in CaseListScreen.
            if (store.currentCase?.id === e.id) {
              store.clearCurrentCase()
              store.setSaveConflict('This case was deleted by someone else — returning you to the case list.')
            }
            break
          }
          default:
            break
        }
      }
    })
  }, [])
}

// ── Stage 4 live-freshness: "N new — refresh" counter for a LIST ──────
// A list component calls this with the entity it shows. It counts remote
// op:'create' events for that entity (echo-suppressed upstream — the creator
// already has their own create, so it never counts their own) and returns the
// running count + a reset. The component renders a NON-DESTRUCTIVE indicator
// from the count and, on click, refetches its list (respecting its current
// filters/sort/page) then resets — we never blind-insert into a filtered list.
// The count is APPROXIMATE: it counts creates of the entity type, not whether
// the new row matches the viewer's active filter/scope (the refetch is the
// source of truth and corrects any over-count). Graceful: socket down → no
// events → count stays 0 → no indicator (new rows appear on the next manual
// load). Scope could refine relevance later; v1 keeps it simple.
//
// ── Stage 5: coalesce the import/bulk CREATE burst (receiver-side debounce) ──
// A bulk import is a client-side loop that creates N rows, so the backend emits
// a burst of op:'create' events in quick succession. Incrementing the VISIBLE
// count per event would flicker (1 → 2 → … → N). Instead each event bumps a
// synchronous accumulator (pendingRef) and schedules a short trailing debounce;
// the visible count updates ONCE per quiet window, settling smoothly to the
// right total. A MAX-WAIT guard flushes periodically so a long continuous
// stream still surfaces progress rather than starving until it ends. The count
// stays ACCURATE (the refetch is the source of truth regardless); this only
// changes WHEN the number updates. Generic — it smooths any rapid create burst,
// not just imports, with no import-specific code. Backend still sends N events
// (bounded by import size, firm-scoped, best-effort) — acceptable for typical
// imports; see the stage report for the large-import sender-suppression flag.
const NEW_ITEMS_DEBOUNCE_MS = 600
const NEW_ITEMS_MAX_WAIT_MS = 2000
export const useNewItemsCount = (
  entity: PresenceEntityChanged['entity'],
): [number, () => void] => {
  const [count, setCount] = useState(0)
  const pendingRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firstPendingAtRef = useRef<number | null>(null)

  useEffect(() => {
    const flush = (): void => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
      firstPendingAtRef.current = null
      const n = pendingRef.current
      pendingRef.current = 0
      if (n > 0) setCount((c) => c + n)
    }
    const unsubscribe = presence.subscribeEntityChanged((e) => {
      if (e.entity !== entity || e.op !== 'create') return
      pendingRef.current += 1
      const now = Date.now()
      if (firstPendingAtRef.current === null) firstPendingAtRef.current = now
      // Trailing debounce — resets on each event so a tight burst coalesces…
      if (timerRef.current) clearTimeout(timerRef.current)
      // …but if the burst has been running longer than MAX_WAIT, flush now so a
      // long continuous import still shows progress instead of starving.
      if (now - firstPendingAtRef.current >= NEW_ITEMS_MAX_WAIT_MS) { flush(); return }
      timerRef.current = setTimeout(flush, NEW_ITEMS_DEBOUNCE_MS)
    })
    return () => {
      unsubscribe()
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    }
  }, [entity])

  // Reset clears the visible count AND any buffered burst so a refetch starts
  // the tally cleanly from zero.
  const reset = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    pendingRef.current = 0
    firstPendingAtRef.current = null
    setCount(0)
  }, [])
  return [count, reset]
}
